import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { BookCard } from '../components/library/BookCard';
import { BookDetailsDialog } from '../components/library/BookDetailsDialog';
import { MaterialsSection } from '../components/library/MaterialsSection';
import { Search, ArrowLeft, BookOpen, Sparkles, BookMarked, NotebookPen } from 'lucide-react';
import '../library.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_FILTERS = [
  { id: 'all', label: 'Всички възрасти' },
  { id: '4-5', label: '4–5 год.', min: 4, max: 5 },
  { id: '6-7', label: '6–7 год.', min: 6, max: 7 },
  { id: '8+', label: '8+ год.', min: 8, max: 99 },
];

export default function LibraryPage() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Всички');
  const [ageFilter, setAgeFilter] = useState('all');
  const [selectedBook, setSelectedBook] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/library/books`)
      .then((res) => setBooks(res.data))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(books.map((b) => b.category))].sort();
    return ['Всички', ...cats];
  }, [books]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const age = AGE_FILTERS.find((a) => a.id === ageFilter);
    return books.filter((b) => {
      if (category !== 'Всички' && b.category !== category) return false;
      if (age?.min && (b.age < age.min || b.age > age.max)) return false;
      if (q && !b.title.toLowerCase().includes(q) && !b.author.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [books, search, category, ageFilter]);

  return (
    <div data-testid="library-page" className="library-bg min-h-screen">
      {/* Хедър */}
      <header className="sticky top-0 z-20 bg-[#fdf6ea]/85 backdrop-blur-md border-b border-amber-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00966E] flex items-center justify-center">
              <BookMarked className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="library-font font-bold text-stone-800 leading-none">Библиотека Азбукарче</h1>
              <p className="text-[11px] text-stone-500 mt-0.5">Чети и мечтай</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="materials-nav-btn"
              onClick={() => document.getElementById('materials')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden sm:inline-flex items-center gap-2 text-sm text-stone-600 hover:text-amber-600 transition-colors font-medium"
            >
              <NotebookPen className="h-4 w-4" /> Материали
            </button>
            <Link
              to="/"
              data-testid="library-back-to-login"
              className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#00966E] transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" /> Към входа
            </Link>
          </div>
        </div>
      </header>

      {/* Херо секция */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-6 relative">
        <Sparkles className="float-deco absolute right-8 top-8 h-8 w-8 text-amber-400/70 hidden sm:block" />
        <BookOpen className="float-deco-slow absolute right-28 top-20 h-6 w-6 text-[#00966E]/50 hidden sm:block" />
        <h2 className="library-font text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-800 max-w-2xl leading-tight">
          Вълшебната <span className="text-[#00966E]">библиотека</span>
        </h2>
        <blockquote data-testid="library-quote" className="mt-4 max-w-xl">
          <p className="library-font text-stone-600 text-sm sm:text-base italic leading-relaxed">
            „Колкото повече четеш, толкова повече ще научиш. Колкото повече научиш, толкова по-далеч ще стигнеш."
          </p>
          <footer className="text-xs text-amber-600 font-semibold mt-1.5">— Д-р Сюс</footer>
        </blockquote>

        {/* Търсене */}
        <div className="relative max-w-md mt-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            data-testid="library-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Потърси книга или автор…"
            className="pl-11 h-12 rounded-full border-amber-200 bg-white/80 focus-visible:ring-[#00966E]"
          />
        </div>

        {/* Категории */}
        <div className="flex flex-wrap gap-2 mt-5">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-chip-${cat}`}
              onClick={() => setCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                category === cat
                  ? 'bg-[#00966E] text-white shadow'
                  : 'bg-white/80 text-stone-600 hover:bg-amber-100 border border-amber-200/70'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Възраст */}
        <div className="flex flex-wrap gap-2 mt-3">
          {AGE_FILTERS.map((a) => (
            <button
              key={a.id}
              data-testid={`age-chip-${a.id}`}
              onClick={() => setAgeFilter(a.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                ageFilter === a.id
                  ? 'bg-amber-500 text-white shadow'
                  : 'bg-white/70 text-stone-500 hover:bg-amber-100 border border-amber-200/70'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </section>

      {/* Книги */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 mt-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse">
                <div className="aspect-[2/3] bg-amber-100 rounded-xl" />
                <div className="h-3 bg-amber-100 rounded mt-3 w-3/4" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="library-no-results" className="text-center py-20">
            <BookOpen className="h-12 w-12 text-amber-300 mx-auto mb-4" />
            <p className="library-font text-stone-600 font-semibold">Няма намерени книги</p>
            <p className="text-sm text-stone-400 mt-1">Опитай с друга дума или категория</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-stone-400 mb-4">{filtered.length} книги</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-8">
              {filtered.map((book, i) => (
                <BookCard key={book.id} book={book} index={i} onClick={() => setSelectedBook(book)} />
              ))}
            </div>
          </>
        )}
      </main>

      <MaterialsSection />

      <footer className="border-t border-amber-200/60 py-6 text-center text-xs text-stone-400">
        БНУ Азбукарче · Електронна библиотека за деца и родители
      </footer>

      <BookDetailsDialog book={selectedBook} onClose={() => setSelectedBook(null)} />
    </div>
  );
}
